import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Loader2, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { useModuleDetailsQuery, CourseModule } from '../../../api/studentCoursesApi';
import ModuleCompleteButton from '../common/ModuleCompleteButton';
import { tokenService } from '../../../services/TokenService';
import './BookModuleStyles.css';
import '../page/PageModuleStyles.css';

interface BookModuleContentProps {
  contextId: string;
  moduleId: string;
  module: CourseModule | null;
  headerColor?: string;
}

const BookModuleContent: React.FC<BookModuleContentProps> = ({ contextId, moduleId, module, headerColor }) => {
  const token = tokenService.getToken();
  const [bookContent, setBookContent] = useState<string>('');
  const [isFetchingContent, setIsFetchingContent] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [currentChapter, setCurrentChapter] = useState(-1); // -1 means show intro
  const [chapters, setChapters] = useState<any[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { data: details, isLoading, error } = useModuleDetailsQuery(
    contextId,
    moduleId,
    'book',
    token,
    {
      enabled: !!contextId && !!moduleId,
    }
  );

  // Fetch book content
  useEffect(() => {
    const fetchBookContent = async () => {
      console.log('BookModuleContent - details:', details);
      console.log('BookModuleContent - module:', module);
      
      // First try to get content from API response
      if (details) {
        console.log('Checking details for content...');
        
        // Handle book chapters if available
        if (details.chapters && Array.isArray(details.chapters)) {
          console.log('Found chapters:', details.chapters);
          setChapters(details.chapters);

          // Empty chapters array — show no-content state, skip intro
          if (details.chapters.length === 0) {
            setBookContent('');
            return;
          }

          // Chapters available — auto-start at first chapter if not yet set
          const idx = currentChapter >= 0 ? currentChapter : 0;
          if (currentChapter === -1) {
            setCurrentChapter(0);
            return; // re-render will trigger effect again with idx=0
          }

          const chapterContent = details.chapters[idx]?.content || '';
          if (chapterContent) {
            console.log('Setting chapter content:', chapterContent);
            setBookContent(chapterContent);
            return;
          }
        }
        
        // Show intro/description/content when no specific chapter is selected
        // (only when chapters field is absent, not when it's explicitly empty)
        if (!details.chapters || !Array.isArray(details.chapters)) {
          const apiContent = details.intro || details.description || details.content || '';
          if (apiContent) {
            console.log('Setting API content from intro/description/content:', apiContent);
            setBookContent(apiContent);
            return;
          }
        }
      }

      // Try to fetch from contents if available
      if (module?.contents && module.contents.length > 0) {
        console.log('Trying module.contents:', module.contents);
        const htmlFile = module.contents.find(c => 
          c.filename === 'index.html' || 
          c.type === 'content' ||
          c.mimetype?.includes('text/html')
        );
        
        if (htmlFile?.fileurl) {
          console.log('Found fileurl, fetching:', htmlFile.fileurl);
          setIsFetchingContent(true);
          setFetchError(null);
          
          try {
            const response = await fetch(htmlFile.fileurl);
            if (!response.ok) {
              throw new Error(`Failed to fetch content: ${response.statusText}`);
            }
            const html = await response.text();
            console.log('Fetched HTML content:', html);
            setBookContent(html);
          } catch (err) {
            console.error('Error fetching book content:', err);
            setFetchError(err instanceof Error ? err.message : 'Failed to load content');
          } finally {
            setIsFetchingContent(false);
          }
        } else {
          console.log('No fileurl found in contents');
        }
      } else {
        console.log('No module.contents available');
      }
    };

    if (details || module?.contents) {
      fetchBookContent();
    }
  }, [details, module?.contents, currentChapter]);

  const handleNextChapter = () => {
    if (currentChapter < chapters.length - 1) {
      setCurrentChapter(currentChapter + 1);
      contentRef.current?.scrollTo({ top: 0 });
    }
  };

  const handlePreviousChapter = () => {
    if (currentChapter > -1) {
      setCurrentChapter(currentChapter - 1);
      contentRef.current?.scrollTo({ top: 0 });
    }
  };
  
  const handleStartReading = () => {
    if (chapters.length > 0) {
      setCurrentChapter(0);
    }
  };

  // Loading State
  if (isLoading || isFetchingContent) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
          <span className="text-gray-600">
            {isFetchingContent ? 'Loading book content...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  // Error State
  if (error || fetchError) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 mb-1">
              Failed to load book content
            </h3>
            <p className="text-sm text-red-700">
              {fetchError || (error instanceof Error ? error.message : 'An unknown error occurred')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Book Content
  if (bookContent) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* ── Header ── */}
        <div className="px-6 py-5 overflow-hidden" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
          <div className="flex items-start gap-4">
            <div className="rounded-lg p-2.5 shrink-0" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
              <BookOpen className="h-6 w-6" style={{ color: headerColor || '#374151' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1">Book</p>
              <h2 className="text-xl font-bold leading-snug text-gray-900">{module?.name || 'Book'}</h2>
            </div>
          </div>
        </div>
        {/* Chapter breadcrumb */}
        {currentChapter >= 0 && chapters[currentChapter]?.title && (
          <div className="px-6 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <span className="text-xs text-gray-500">Chapter {currentChapter + 1}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="text-sm font-medium text-gray-700">{chapters[currentChapter].title}</span>
          </div>
        )}
        <div ref={contentRef} className="px-4 sm:px-6 max-h-[70vh] overflow-y-auto">
          <div
            className="moodle-content"
            dangerouslySetInnerHTML={{ __html: bookContent }}
          />
        </div>

        {/* Navigation and Actions */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            {/* Chapter Navigation */}
            {chapters.length > 0 && (
              <div className="flex items-center gap-2">
                {currentChapter === -1 ? (
                  <Button
                    onClick={handleStartReading}
                    variant="default"
                    size="sm"
                  >
                    Start Reading
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handlePreviousChapter}
                      disabled={currentChapter === -1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {currentChapter === 0 ? 'Intro' : 'Previous'}
                    </Button>
                    <span className="text-sm text-gray-600">
                      Chapter {currentChapter + 1} of {chapters.length}
                    </span>
                    <Button
                      onClick={handleNextChapter}
                      disabled={currentChapter === chapters.length - 1}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </>
                )}
              </div>
            )}
            {chapters.length === 0 && <div />}
            <ModuleCompleteButton contextId={contextId} moduleId={moduleId} variant="footer" />
          </div>
        </div>
      </div>
    );
  }

  // No Content State (chapters empty or no content)
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12">
      <div className="text-center">
        <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        {module?.name && (
          <p className="text-base font-semibold text-gray-700 mb-2">{module.name}</p>
        )}
        <p className="text-sm text-gray-500">No content has been added yet</p>
      </div>
    </div>
  );
};

export default BookModuleContent;
